import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useI18n, useLocalized } from "@/lib/i18n";
import { useCurrency, isTaxExempt } from "@/lib/currency";
import { CurrencyValue } from "@/components/CurrencyValue";
import { ProductBadges } from "@/components/ProductBadges";
import { QuickView } from "@/components/QuickView";
import { Eye, Heart, ShoppingCart, Clock, Check, Loader2, Scale } from "lucide-react";
import { useWishlist } from "@/lib/wishlist-context";
import { useCompare } from "@/lib/compare-context";
import { useTheme } from "@/lib/theme-context";
import { resolveImageSrc, buildAnySrcSet, PRODUCT_SIZES } from "@/lib/imgSrc";
import { useQueryClient } from "@tanstack/react-query";
import { getGetCartQueryKey } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { getCartSessionId } from "@/lib/cart";

type CardStyle = "default" | "minimal" | "bordered" | "overlay" | "elegant" | "compact";

interface ProductCardProps {
  product: any;
  style?: CardStyle;
  priority?: boolean;
  // Per-block overrides (homepage layout editor). When set, take precedence
  // over the global theme defaults so individual category blocks can show
  // square / circular / rectangular product images independently.
  imageRatio?: string;
  imageRadius?: string;
}

function MiniCountdown({ endDate }: { endDate: string }) {
  const { lang } = useI18n();
  const [timeLeft, setTimeLeft] = useState({ d: 0, h: 0, m: 0, s: 0 });
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const calc = () => {
      const diff = new Date(endDate).getTime() - Date.now();
      if (diff <= 0) { setExpired(true); return; }
      setTimeLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    calc();
    const iv = setInterval(calc, 1000);
    return () => clearInterval(iv);
  }, [endDate]);

  if (expired) return null;

  const pad = (n: number) => n.toString().padStart(2, "0");
  return (
    <div className="flex items-center gap-1 bg-red-600 text-white px-2 py-1 rounded text-[10px] font-mono">
      <Clock className="h-3 w-3" />
      <span>{pad(timeLeft.d)}:{pad(timeLeft.h)}:{pad(timeLeft.m)}:{pad(timeLeft.s)}</span>
    </div>
  );
}

// Renders per-image color swatches derived from `product.imageColorLinks`,
// which now maps imageUrl → hex string (e.g. "#a52a2a") set freely by the
// merchant in admin → Images via the native color picker. One swatch per
// linked image; hovering swaps the main <img> to that image. Decoupled
// from the legacy named-color option values — each photo has its own shade
// because product images rarely match predefined color names 1:1.
function ColorSwatches({
  imageColorLinks,
  onImageHover,
  onColorLeave,
}: {
  imageColorLinks?: Record<string, string | number>;
  onImageHover?: (imageUrl: string) => void;
  onColorLeave?: () => void;
}) {
  if (!imageColorLinks || typeof imageColorLinks !== "object") return null;
  const entries = Object.entries(imageColorLinks)
    .filter(([url, val]) => url && typeof val === "string" && /^#[0-9a-fA-F]{6}$/.test(val)) as [string, string][];
  if (entries.length === 0) return null;

  return (
    <div
      className="flex items-center gap-1 mt-1"
      onMouseLeave={onColorLeave}
    >
      {entries.slice(0, 5).map(([url, hex]) => (
        <button
          key={url}
          type="button"
          aria-label={hex}
          onMouseEnter={() => onImageHover?.(url)}
          onMouseLeave={() => onColorLeave?.()}
          onFocus={() => onImageHover?.(url)}
          onBlur={() => onColorLeave?.()}
          onClick={(e) => { e.preventDefault(); onImageHover?.(url); }}
          className="w-3.5 h-3.5 rounded-full border border-gray-300 inline-block cursor-pointer transition-transform hover:scale-125 focus:scale-125 focus:outline-none focus:ring-1 focus:ring-primary"
          style={{ backgroundColor: hex }}
          data-testid={`color-swatch-${hex.slice(1)}`}
        />
      ))}
      {entries.length > 5 && (
        <span className="text-[10px] text-gray-400">+{entries.length - 5}</span>
      )}
    </div>
  );
}

export function ProductCard({ product, style: styleProp, priority = false, imageRatio: imageRatioProp, imageRadius: imageRadiusProp }: ProductCardProps) {
  const { lang } = useI18n();
  const l = useLocalized();
  const { formatPrice } = useCurrency();
  const theme = useTheme();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [quickView, setQuickView] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  // Tracks which color swatch is currently hovered/focused on this card so the
  // main image can swap to that linked image. After the hex-only refactor we
  // key the hover directly by image URL (the swatch's value), avoiding an
  // extra lookup. Null → show the default product image.
  const [hoveredImageUrl, setHoveredImageUrl] = useState<string | null>(null);

  const cardStyle = styleProp || theme?.productCardStyle || "default";
  const hoverEffect = theme?.productImageHover || "swap";
  const imgLoad: "lazy" | "eager" = theme?.lazyLoadImages === "false" ? "eager" : "lazy";
  const productName = l(product, "name") || (lang === "ar" ? "منتج" : "Product");
  const inWishlist = isInWishlist(product.id);
  const showQuickView = theme?.showQuickView !== "false";
  const showWishlistBtn = theme?.showWishlistBtn !== "false" && theme?.wishlistEnabled !== "false";
  const showBadges = theme?.showProductBadges !== "false";
  const badgePos = (theme?.badgePosition as string) || "top-start";
  const badgePosClass =
    badgePos === "top-end" ? "top-2 end-2"
    : badgePos === "bottom-start" ? "bottom-2 start-2"
    : badgePos === "bottom-end" ? "bottom-2 end-2"
    : "top-2 start-2";
  const badgeAtBottom = badgePos.startsWith("bottom-");
  const countdownPosClass = badgeAtBottom ? "top-2 start-2" : "bottom-2 start-2";
  // For card styles whose hover overlay sits at bottom-0 (quick-view bar),
  // raise the countdown to bottom-12 to clear it when not displaced by badges.
  const countdownPosClassRaised = badgeAtBottom ? "top-2 start-2" : "bottom-12 start-2";
  const showRating = theme?.showProductRating === "true" || theme?.showAverageRating !== "false";
  const showRevCount = theme?.showReviewCount !== "false";
  const showDiscountPercent = theme?.showDiscountPercent === "true";
  // hideAddToCart is the explicit "no cart on storefront" toggle (e.g. catalog-only
  // mode). It overrides showAddToCartBtn when set, and triggers the price
  // replacement text below.
  const hideAddToCart = theme?.hideAddToCart === "true";
  const showAddToCartBtn = !hideAddToCart && theme?.showAddToCartBtn !== "false";
  const showCompareBtn = theme?.showCompareBtn === "true";
  const priceReplacement = lang === "ar"
    ? ((theme?.priceReplacementTextAr as string) || (theme?.priceReplacementText as string) || "")
    : ((theme?.priceReplacementText as string) || (theme?.priceReplacementTextAr as string) || "");
  const { toggleCompare, isInCompare } = useCompare();
  const inCompare = isInCompare(product.id);
  const showBrandName = theme?.showBrandName === "true";
  const showColorSwatches = theme?.showColorSwatches === "true";
  const imageRatio = imageRatioProp || theme?.productImageRatio || "4/5";
  // 3-tier fallback: product-specific → global border radius → 12px baseline.
  // Same chain as Button.tsx so that setting the global "Corner Radius" in
  // Admin → Appearance flows through to every product image automatically,
  // and a product-specific override (Admin → Appearance → Product images)
  // wins when set.
  const imageRadius = imageRadiusProp || theme?.productImageRadius || theme?.borderRadius || "12px";
  const isCircular = String(imageRadius).trim() === "9999px";
  // Circular product images intentionally hide the discount badge and sale
  // countdown — these overlays don't fit inside a circle without clipping
  // or floating awkwardly outside the visual frame (merchant feedback).
  const hideOverlays = isCircular;
  const effectiveBadgePosClass = hideOverlays ? "hidden" : badgePosClass;
  const effectiveCountdownPosClass = hideOverlays ? "hidden" : countdownPosClass;
  const effectiveCountdownPosClassRaised = hideOverlays ? "hidden" : countdownPosClassRaised;

  // Per-image hex map (imageUrl → "#rrggbb"). Hovering a swatch directly
  // swaps the main <img> to that image URL — no option-value indirection
  // needed since we now key swatches by URL.
  const imageColorLinks: Record<string, string | number> = (product.imageColorLinks && typeof product.imageColorLinks === "object") ? product.imageColorLinks : {};
  const displayImageUrl = hoveredImageUrl || product.imageUrl;
  // The main image is often duplicated as images[0] (e.g. OpenCart imports),
  // which made hover-swap appear broken (swapping to an identical picture).
  // Pick the first gallery image that actually differs from the main one.
  const secondImage = product.images?.find((u: string) => u && u !== product.imageUrl) || null;
  const canSwapImage = hoverEffect === "swap" && secondImage && !imgError;
  const showSecondImg = hovered && canSwapImage;
  const hasSaleCountdown = product.salePrice && product.saleEndDate && new Date(product.saleEndDate) > new Date();

  const discountPct = product.salePrice && product.price > 0 ? Math.round((1 - product.salePrice / product.price) * 100) : 0;

  const taxExempt = isTaxExempt((product as any).taxClassId);
  const PriceDisplay = () => (
    <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5">
      {hideAddToCart && priceReplacement ? (
        <span className="text-sm font-medium whitespace-nowrap text-gray-700">{priceReplacement}</span>
      ) : product.salePrice ? (
        <>
          <span className="text-sm font-medium text-red-600 whitespace-nowrap"><CurrencyValue value={product.salePrice} taxed taxExempt={taxExempt} /></span>
          <span className="text-xs text-gray-600 line-through whitespace-nowrap"><CurrencyValue value={product.price} taxed taxExempt={taxExempt} /></span>
          {showDiscountPercent && discountPct > 0 && (
            <span className="text-xs text-red-700 font-semibold whitespace-nowrap">-{discountPct}%</span>
          )}
        </>
      ) : (
        <span className="text-sm font-medium whitespace-nowrap"><CurrencyValue value={product.price} taxed taxExempt={taxExempt} /></span>
      )}
    </div>
  );

  const RatingStars = () => {
    if (!showRating || !product.avgRating) return null;
    return (
      <div className="flex items-center gap-0.5 mb-1">
        {[1,2,3,4,5].map(i => (
          <svg key={i} className={`h-3 w-3 ${i <= Math.round(product.avgRating) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`} viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
        ))}
        {showRevCount && product.reviewCount > 0 && <span className="text-xs text-gray-600 ms-1">({product.reviewCount})</span>}
      </div>
    );
  };

  const hoverClass = hoverEffect === "zoom" ? "group-hover:scale-110" :
    hoverEffect === "fade" ? "group-hover:opacity-80" :
    hoverEffect === "slide" ? "group-hover:translate-y-[-4px]" :
    hoverEffect === "swap" && !canSwapImage ? "group-hover:scale-105" :
    hoverEffect === "none" ? "" : "group-hover:scale-105";

  const ratioKey = String(imageRatio).replace(":", "/");
  const aspectClass =
    ratioKey === "1/1" ? "aspect-square" :
    ratioKey === "3/4" ? "aspect-[3/4]" :
    ratioKey === "4/3" ? "aspect-[4/3]" :
    ratioKey === "16/9" ? "aspect-video" :
    ratioKey === "auto" ? "" : "aspect-[4/5]";

  const BrandLabel = () => {
    if (!showBrandName || !product.brandName) return null;
    return <p className="text-xs text-gray-700 uppercase tracking-wider mb-0.5 font-medium">{product.brandName}</p>;
  };

  const ImageSection = ({ className = "", priority = false }: { className?: string; priority?: boolean }) => (
    <div
      className={`relative overflow-hidden bg-gray-50 ${className}`}
      style={{ borderRadius: imageRadius }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {product.imageUrl && !imgError ? (
        <>
          {!imgLoaded && <div className="absolute inset-0 bg-gray-100 animate-pulse" />}
          <img
            src={resolveImageSrc(displayImageUrl, 800) as string | undefined}
            srcSet={buildAnySrcSet(displayImageUrl, [320, 480, 640, 800], [320, 480, 640, 800])}
            sizes={PRODUCT_SIZES}
            alt={productName}
            width={400}
            height={500}
            loading={priority ? "eager" : imgLoad}
            decoding="async"
            {...(priority ? { fetchPriority: "high" as any } : {})}
            className={`object-cover w-full h-full transition-all duration-500 ${
              showSecondImg ? "opacity-0 scale-105" : "opacity-100"
            } ${imgLoaded ? "" : "opacity-0"} ${!showSecondImg ? hoverClass : ""}`}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
          />
          {canSwapImage && secondImage && (
            <img
              src={resolveImageSrc(secondImage, 800) as string | undefined}
              srcSet={buildAnySrcSet(secondImage, [320, 480, 640, 800], [320, 480, 640, 800])}
              sizes={PRODUCT_SIZES}
              alt={`${productName} 2`}
              loading={imgLoad} decoding="async"
              className={`absolute inset-0 object-cover w-full h-full transition-all duration-500 ${
                showSecondImg ? "opacity-100 scale-100" : "opacity-0 scale-110"
              }`}
            />
          )}
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs">

        </div>
      )}
      {showBadges && (
        <div className={`absolute ${effectiveBadgePosClass}`}>
          <ProductBadges product={product} size="sm" />
        </div>
      )}
      {hasSaleCountdown && (
        <div className={`absolute ${effectiveCountdownPosClass}`}>
          <MiniCountdown endDate={product.saleEndDate} />
        </div>
      )}
      {!product.quantity && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
          <span className="bg-white text-black text-[10px] font-medium px-3 py-1.5">
            {lang === "ar" ? "نفذت الكمية" : "SOLD OUT"}
          </span>
        </div>
      )}
    </div>
  );

  if (cardStyle === "minimal") {
    return (
      <>
        <div className="group">
          <Link href={`/product/${product.id}`}>
            <ImageSection className={`${aspectClass} mb-4`} priority={priority} />
          </Link>
          <div className="text-center">
            <RatingStars />
            <Link href={`/product/${product.id}`}>
              <h3 className="text-xs tracking-wider uppercase text-gray-600 mb-1 line-clamp-1 hover:text-primary transition-colors cursor-pointer">{productName}</h3>
            </Link>
            <PriceDisplay />
            {showColorSwatches && <ColorSwatches imageColorLinks={imageColorLinks} onImageHover={setHoveredImageUrl} onColorLeave={() => setHoveredImageUrl(null)} />}
          </div>
        </div>
        {showQuickView && <QuickView product={product} open={quickView} onOpenChange={setQuickView} />}
      </>
    );
  }

  if (cardStyle === "bordered") {
    return (
      <>
        <div className="group border rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
          <Link href={`/product/${product.id}`}>
            <ImageSection className={aspectClass} priority={priority} />
          </Link>
          <div className="p-3">
            <BrandLabel />
            <RatingStars />
            <Link href={`/product/${product.id}`}>
              <h3 className="text-xs md:text-sm text-gray-800 leading-tight mb-2 line-clamp-2 hover:text-primary transition-colors cursor-pointer">{productName}</h3>
            </Link>
            <div className="flex items-center justify-between">
              <div>
                <PriceDisplay />
                {showColorSwatches && <ColorSwatches imageColorLinks={imageColorLinks} onImageHover={setHoveredImageUrl} onColorLeave={() => setHoveredImageUrl(null)} />}
              </div>
              <div className="flex items-center gap-1">
                {showWishlistBtn && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(product.id); }}
                    className={`p-2.5 rounded-full transition-colors ${inWishlist ? "text-red-500" : "text-gray-400 hover:text-red-500"}`}
                    aria-label={lang === "ar" ? `${inWishlist ? "إزالة من" : "إضافة إلى"} المفضلة: ${productName}` : `${inWishlist ? "Remove from" : "Add to"} wishlist: ${productName}`}
                  >
                    <Heart className={`h-4 w-4 ${inWishlist ? "fill-current" : ""}`} aria-hidden="true" />
                  </button>
                )}
                {showQuickView && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickView(true); }}
                    className="p-2.5 rounded-full text-gray-400 hover:text-primary transition-colors"
                    aria-label={lang === "ar" ? `معاينة سريعة: ${productName}` : `Quick view: ${productName}`}
                  >
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
                {showCompareBtn && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleCompare(product.id); }}
                    className={`p-2.5 rounded-full transition-colors ${inCompare ? "text-primary" : "text-gray-400 hover:text-primary"}`}
                    aria-label={lang === "ar" ? `${inCompare ? "إزالة من" : "إضافة إلى"} المقارنة: ${productName}` : `${inCompare ? "Remove from" : "Add to"} compare: ${productName}`}
                  >
                    <Scale className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {showQuickView && <QuickView product={product} open={quickView} onOpenChange={setQuickView} />}
      </>
    );
  }

  if (cardStyle === "overlay") {
    return (
      <>
        <div className="group relative">
          <Link href={`/product/${product.id}`}>
            <ImageSection className="aspect-[3/4] rounded-lg" priority={priority} />
            <div className="absolute bottom-0 start-0 end-0 bg-gradient-to-t from-black/70 to-transparent p-4 rounded-b-lg">
              <h3 className="text-xs text-white leading-tight mb-1 line-clamp-1">{productName}</h3>
              <div className="flex items-center gap-2">
                {product.salePrice ? (
                  <>
                    <span className="text-sm font-medium text-white"><CurrencyValue value={product.salePrice} taxed taxExempt={taxExempt} /></span>
                    <span className="text-xs text-white/60 line-through"><CurrencyValue value={product.price} taxed taxExempt={taxExempt} /></span>
                    {showDiscountPercent && discountPct > 0 && <span className="text-xs text-red-200 font-semibold">-{discountPct}%</span>}
                  </>
                ) : (
                  <span className="text-sm font-medium text-white"><CurrencyValue value={product.price} taxed taxExempt={taxExempt} /></span>
                )}
              </div>
            </div>
          </Link>
          <div className="absolute top-2 end-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {showWishlistBtn && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(product.id); }}
                className={`bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-sm transition-colors ${inWishlist ? "text-red-500" : "text-gray-600 hover:text-red-500"}`}
                aria-label={lang === "ar" ? `${inWishlist ? "إزالة من" : "إضافة إلى"} المفضلة: ${productName}` : `${inWishlist ? "Remove from" : "Add to"} wishlist: ${productName}`}
              >
                <Heart className={`h-4 w-4 ${inWishlist ? "fill-current" : ""}`} aria-hidden="true" />
              </button>
            )}
            {showQuickView && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickView(true); }}
                className="bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-sm text-gray-600 hover:text-primary transition-colors"
                aria-label={lang === "ar" ? `معاينة سريعة: ${productName}` : `Quick view: ${productName}`}
              >
                <Eye className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
        {showQuickView && <QuickView product={product} open={quickView} onOpenChange={setQuickView} />}
      </>
    );
  }

  if (cardStyle === "elegant") {
    return (
      <>
        <div className="group text-center">
          <Link href={`/product/${product.id}`}>
            <div
              className={`relative overflow-hidden bg-gray-50 ${aspectClass} mb-4`}
              style={{ borderRadius: imageRadius }}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
            >
              {product.imageUrl && !imgError ? (
                <>
                  {!imgLoaded && <div className="absolute inset-0 bg-gray-100 animate-pulse" />}
                  <img
                    src={resolveImageSrc(displayImageUrl, 800) as string | undefined}
                    srcSet={buildAnySrcSet(displayImageUrl, [320, 480, 640, 800], [320, 480, 640, 800])}
                    sizes={PRODUCT_SIZES}
                    alt={productName}
                    width={400}
                    height={500}
                    loading={priority ? "eager" : imgLoad}
                    decoding={priority ? "sync" : "async"}
                    {...(priority ? { fetchPriority: "high" as any } : {})}
                    className={`object-cover w-full h-full transition-all duration-700 ${
                      showSecondImg ? "opacity-0 scale-110" : "opacity-100"
                    } ${!showSecondImg ? `${hoverClass} group-hover:brightness-95` : ""} ${imgLoaded ? "" : "opacity-0"}`}
                    onLoad={() => setImgLoaded(true)}
                    onError={() => setImgError(true)}
                  />
                  {canSwapImage && secondImage && (
                    <img
                      src={resolveImageSrc(secondImage, 800) as string | undefined}
                      srcSet={buildAnySrcSet(secondImage, [320, 480, 640, 800], [320, 480, 640, 800])}
                      sizes={PRODUCT_SIZES}
                      alt={`${productName} 2`}
                      loading={imgLoad} decoding="async"
                      className={`absolute inset-0 object-cover w-full h-full transition-all duration-700 ${
                        showSecondImg ? "opacity-100 scale-100" : "opacity-0 scale-110"
                      }`}
                    />
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs">

                </div>
              )}
              {showBadges && (
                <div className={`absolute ${effectiveBadgePosClass}`}>
                  <ProductBadges product={product} size="sm" />
                </div>
              )}
              {hasSaleCountdown && (
                <div className={`absolute ${effectiveCountdownPosClassRaised}`}>
                  <MiniCountdown endDate={product.saleEndDate} />
                </div>
              )}
              {showQuickView && (
                <div className="absolute bottom-0 start-0 end-0 bg-black py-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickView(true); }}
                    className="text-white text-[10px] tracking-[0.2em] uppercase w-full flex items-center justify-center gap-2 min-h-[44px]"
                    aria-label={lang === "ar" ? `معاينة سريعة: ${productName}` : `Quick view: ${productName}`}
                  >
                    <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                    {lang === "ar" ? "معاينة سريعة" : "Quick View"}
                  </button>
                </div>
              )}
            </div>
          </Link>
          <RatingStars />
          <Link href={`/product/${product.id}`}>
            <h3 className="text-xs tracking-wider uppercase text-gray-700 mb-2 line-clamp-1 hover:text-primary transition-colors cursor-pointer">{productName}</h3>
          </Link>
          <PriceDisplay />
          {showColorSwatches && <ColorSwatches imageColorLinks={imageColorLinks} onImageHover={setHoveredImageUrl} onColorLeave={() => setHoveredImageUrl(null)} />}
          <div className="w-6 h-[1px] bg-gray-300 mx-auto mt-3" />
        </div>
        {showQuickView && <QuickView product={product} open={quickView} onOpenChange={setQuickView} />}
      </>
    );
  }

  if (cardStyle === "compact") {
    return (
      <>
        <div className="group flex gap-3 items-center p-2 rounded-lg hover:bg-muted/50 transition-colors">
          <Link href={`/product/${product.id}`} className="shrink-0">
            <div className="w-20 h-20 relative overflow-hidden bg-gray-50" style={{ borderRadius: imageRadius }}>
              {product.imageUrl && !imgError ? (
                <img
                  src={resolveImageSrc(displayImageUrl, 320) as string | undefined}
                  srcSet={buildAnySrcSet(displayImageUrl, [160, 320], [160, 320])}
                  sizes="80px"
                  alt={productName}
                  width={80}
                  height={80}
                  loading={imgLoad} decoding="async"
                  className="object-cover w-full h-full"
                  onLoad={() => setImgLoaded(true)}
                  onError={() => setImgError(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-[8px]">

                </div>
              )}
            </div>
          </Link>
          <div className="flex-1 min-w-0">
            <Link href={`/product/${product.id}`}>
              <h3 className="text-xs font-medium text-gray-800 line-clamp-2 mb-1 hover:text-primary transition-colors">{productName}</h3>
            </Link>
            <RatingStars />
            <PriceDisplay />
            {showColorSwatches && <ColorSwatches imageColorLinks={imageColorLinks} onImageHover={setHoveredImageUrl} onColorLeave={() => setHoveredImageUrl(null)} />}
          </div>
          {showQuickView && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickView(true); }}
              className="p-3 text-gray-400 hover:text-primary transition-colors shrink-0"
              aria-label={lang === "ar" ? `معاينة سريعة: ${productName}` : `Quick view: ${productName}`}
            >
              <Eye className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
        {showQuickView && <QuickView product={product} open={quickView} onOpenChange={setQuickView} />}
      </>
    );
  }

  return (
    <>
      <div className="group flex flex-col h-full">
        <Link href={`/product/${product.id}`}>
          <div
            className={`relative ${aspectClass} overflow-hidden bg-gray-50 mb-3`}
            style={{ borderRadius: imageRadius }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            {product.imageUrl && !imgError ? (
              <>
                {!imgLoaded && <div className="absolute inset-0 bg-gray-100 animate-pulse" />}
                <img
                  src={resolveImageSrc(displayImageUrl, 800) as string | undefined}
                  srcSet={buildAnySrcSet(displayImageUrl, [320, 480, 640, 800], [320, 480, 640, 800])}
                  sizes={PRODUCT_SIZES}
                  alt={productName}
                  width={400}
                  height={500}
                  loading={priority ? "eager" : imgLoad}
                  decoding={priority ? "sync" : "async"}
                  {...(priority ? { fetchPriority: "high" as any } : {})}
                  className={`object-cover w-full h-full transition-all duration-500 ${
                    showSecondImg ? "opacity-0 scale-105" : "opacity-100"
                  } ${!showSecondImg ? hoverClass : ""} ${imgLoaded ? "" : "opacity-0"}`}
                  onLoad={() => setImgLoaded(true)}
                  onError={() => setImgError(true)}
                />
                {canSwapImage && secondImage && (
                  <img
                    src={resolveImageSrc(secondImage, 800) as string | undefined}
                    srcSet={buildAnySrcSet(secondImage, [320, 480, 640, 800], [320, 480, 640, 800])}
                    sizes={PRODUCT_SIZES}
                    alt={`${productName} 2`}
                    loading={imgLoad} decoding="async"
                    className={`absolute inset-0 object-cover w-full h-full transition-all duration-500 ${
                      showSecondImg ? "opacity-100 scale-100" : "opacity-0 scale-110"
                    }`}
                  />
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs">

              </div>
            )}
            {showBadges && (
              <div className={`absolute ${effectiveBadgePosClass}`}>
                <ProductBadges product={product} size="sm" />
              </div>
            )}
            {hasSaleCountdown && (
              <div className={`absolute ${effectiveCountdownPosClassRaised}`}>
                <MiniCountdown endDate={product.saleEndDate} />
              </div>
            )}
            {!product.quantity && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <span className="bg-white text-black text-[10px] font-medium px-3 py-1.5">
                  {lang === "ar" ? "نفذت الكمية" : "SOLD OUT"}
                </span>
              </div>
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
            {showQuickView && (
              <div className="absolute bottom-3 start-0 end-0 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickView(true); }}
                  className="bg-white/90 backdrop-blur-sm text-black px-4 py-3 text-[10px] tracking-[0.15em] uppercase font-medium flex items-center gap-1.5 hover:bg-white transition-colors shadow-lg min-h-[44px]"
                  aria-label={lang === "ar" ? `معاينة سريعة: ${productName}` : `Quick view: ${productName}`}
                >
                  <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                  {lang === "ar" ? "معاينة سريعة" : "Quick View"}
                </button>
              </div>
            )}
            {showCompareBtn && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleCompare(product.id); }}
                className={`absolute top-2 end-2 z-10 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-sm transition-colors ${inCompare ? "text-primary" : "text-gray-600 hover:text-primary"}`}
                aria-label={lang === "ar" ? `${inCompare ? "إزالة من" : "إضافة إلى"} المقارنة: ${productName}` : `${inCompare ? "Remove from" : "Add to"} compare: ${productName}`}
              >
                <Scale className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </Link>
        <div className="flex flex-col flex-1">
          <BrandLabel />
          <RatingStars />
          <Link href={`/product/${product.id}`}>
            <h3 className="text-xs md:text-sm text-gray-800 leading-tight mb-1 line-clamp-2 hover:text-primary transition-colors cursor-pointer">{productName}</h3>
          </Link>
          <PriceDisplay />
          {showColorSwatches && <ColorSwatches imageColorLinks={imageColorLinks} onImageHover={setHoveredImageUrl} onColorLeave={() => setHoveredImageUrl(null)} />}
          {showAddToCartBtn && product.quantity > 0 && (
            <button
              disabled={addingToCart}
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (addingToCart || addedToCart) return;
                setAddingToCart(true);
                try {
                  const sessionId = getCartSessionId();
                  const res = await fetch(`${import.meta.env.BASE_URL || "/"}api/cart`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ sessionId, productId: product.id, quantity: 1 }),
                  });
                  if (res.ok) {
                    queryClient.invalidateQueries({ queryKey: getGetCartQueryKey({ sessionId }) });
                    setAddedToCart(true);
                    setTimeout(() => setAddedToCart(false), 2000);
                    import("@/lib/analytics").then(m => m.trackAddToCart(product.id));
                    toast({
                      title: lang === "ar" ? "تمت الإضافة للسلة" : "Added to cart",
                      description: productName,
                    });
                    if (theme?.cartOpenOnAdd === "true") {
                      import("@/lib/cart").then(m => m.openSideCart());
                    }
                  } else {
                    toast({
                      variant: "destructive",
                      description: lang === "ar" ? "حدث خطأ أثناء الإضافة" : "Error adding to cart",
                    });
                  }
                } catch {
                  toast({
                    variant: "destructive",
                    description: lang === "ar" ? "حدث خطأ أثناء الإضافة" : "Error adding to cart",
                  });
                } finally {
                  setAddingToCart(false);
                }
              }}
              className="btn-theme-primary mt-auto w-full flex items-center justify-center gap-1.5 text-[10px] tracking-wider uppercase py-1.5 disabled:opacity-50"
            >
              {addingToCart ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : addedToCart ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <ShoppingCart className="h-3.5 w-3.5" />
              )}
              {addedToCart
                ? (lang === "ar" ? "تمت الإضافة" : "Added!")
                : (lang === "ar" ? "أضف للسلة" : "Add to Cart")
              }
            </button>
          )}
        </div>
      </div>
      {showQuickView && <QuickView product={product} open={quickView} onOpenChange={setQuickView} />}
    </>
  );
}
